import React, { useEffect, useRef } from 'react';
import ReactModal from 'react-modal';
import cn from 'classnames';

import FLAG from 'config/flag.singleton';
import S from 'constants/string.const';
import { useTheme } from 'providers/Theme.provider';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import logger from 'utils/logger.util';
import reactChildrenLike from 'utils/react/childrenLike.util';
import hasReactChildren from 'utils/react/hasReactChildren.util';

import CN from './SalonModal.module.scss';

const L = logger('SalonModal');

const MODAL_STYLE = Object.freeze({ overlay: { zIndex: 500 } });
const MODAL_CLASS = Object.freeze({ base: CN.wrap, afterOpen: CN.wrap, beforeClose: CN.wrap });

const Body = ({ children }) => <>{children}</>;
const Footer = ({ children }) => <>{children}</>;
const Title = ({ children }) => <>{children}</>;
const Header = ({ children }) => <>{children}</>;

const SalonModal = ({
  tag: tagFromProps,
  title,
  isOpen,
  children,
  variant,
  banner,
  withFooter,
  withGradient,
  isHighlighted,
  mode,
  shouldCloseOnOverlayClick = true,
  shouldCloseOnEsc = true,
}) => {
  const tag = fromAnyToTrimmed(tagFromProps);

  const prevOpen = useRef();
  const nextOpen = fromAnyToBool(isOpen);

  const { isDarkMode } = useTheme();

  // stops the modal component from complaining in browser's console
  // noinspection JSUnresolvedFunction
  ReactModal.setAppElement('body');

  const headChildren = reactChildrenLike(Header, children);
  const titleChildren = reactChildrenLike(Title, children);
  const bodyChildren = reactChildrenLike(Body, children);
  const footChildren = reactChildrenLike(Footer, children);

  const tally = `${headChildren.length}/${titleChildren.length}|${bodyChildren.length}|${footChildren.length}`;
  const emojiTag = S.space + (isOpen ? S.circleGreen : S.circleRed) + S.space + tally + S.space + tag;

  const isOpenRef = useRef(isOpen); // to avoid re-triggering the useEffect
  useEffect(() => {
    if (!FLAG.loggerSalonModalMount) return;
    L.debug('()', S.squareGreen + emojiTag, { isOpen: isOpenRef.current });
    return () => void L.debug('()', S.squareRed + emojiTag);
  }, [emojiTag]);

  useEffect(() => {
    if (prevOpen.current === nextOpen) return;

    prevOpen.current = nextOpen;
    L.debug('()', S.squareBlue + emojiTag);
  }, [nextOpen, emojiTag]);

  return (
    <ReactModal
      ariaHideApp
      className={MODAL_CLASS}
      closeTimeoutMS={0}
      contentLabel={title}
      isOpen={isOpen}
      role="dialog"
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      shouldCloseOnEsc={shouldCloseOnEsc}
      style={MODAL_STYLE}
    >
      <div
        data-bem="SalonModal"
        data-tag={tag}
        className={cn({
          [CN.dialog]: true,
          [CN[`${variant}Variant`]]: variant,
          [CN[`${banner}Banner`]]: banner,
          [CN.withFooter]: withFooter,
          [CN.withGradient]: withGradient,
          [CN.isHighlighted]: isHighlighted,
          [CN[`${mode}Mode`]]: mode && isDarkMode, //  mode="dark"
        })}
      >
        <div
          data-bem="SalonModal__header"
          data-tag={tag}
          className={cn({
            [CN.header]: true,
            [CN.empty]: !hasReactChildren(titleChildren) && !hasReactChildren(headChildren),
          })}
        >
          {headChildren}

          {hasReactChildren(titleChildren) && (
            <h2 data-bem="SalonModal__title" data-tag={tag}>
              {titleChildren}
            </h2>
          )}
        </div>

        <div data-bem="SalonModal__body" data-tag={tag} className={CN.body}>
          {bodyChildren}
        </div>
        <div data-bem="SalonModal__footer" data-tag={tag} className={CN.footer}>
          {footChildren}
        </div>
      </div>
    </ReactModal>
  );
};

// noinspection JSUnusedGlobalSymbols

Object.assign(SalonModal, { Body, Footer, Title, Header });

export default SalonModal;
